/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../base.component';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import { DrawerService, LanguageService, LogService } from '../../services';
import { ErrorResponseModel } from '../../models';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-help',
    styleUrls: ['./help.component.scss'],
    templateUrl: './help.component.html',
})
export class HelpComponent extends BaseComponent implements OnInit {
    /**
     * Help content
     */
    @Input() public tag: string;

    /**
     * Help width (optional)
     */
    @Input() public width: number | 'auto' = 250;

    /**
     * Force tooltip right.
     */
    @Input() public forceRight: boolean = false;

    /**
     * Title.
     */
    public title: string;

    /**
     * Content.
     */
    public content: string;

    /**
     * Help text.
     */
    public helpText: { title?: any; summary?: any; text?: any; };

    /**
     * If there is no help text hide the icon.
     */
    public helpTextAvailable: boolean = true;

    /**
     * Store failed drawer content requests
     * @private
     */
    private failedDrawerContentRequests: string[] = [];

    constructor(
        private drawerService: DrawerService,
        private http: HttpClient,
        private languageService: LanguageService,
        private logService: LogService
    ) {
        super();

    }

    ngOnInit() {
        const loadedHelpText: string = this.languageService.getHelpItemByKey(this.tag);
        this.helpTextAvailable = !!loadedHelpText;
    }

    /**
     * Get the help text based on input tag.
     */
    @HostListener('focusin')
    @HostListener('mouseenter')
    getHelpTextFromTag(): void {
        const loadedHelpText: object = this.languageService.getHelpItemByKey(this.tag);
        this.helpText = loadedHelpText ? loadedHelpText : this.noText();
        this.title = this.helpText.title;
        this.content = this.helpText.summary;
    }

    /**
     * Empty the help text object
     */
    @HostListener('focusout')
    @HostListener('mouseleave')
    hide(): void {
        this.helpText = {};
    }

    /**
     * Opens the draw if help is clicked.
     */
    @HostListener('click')
    click(): void {
        const drawerParts: string[] = this.tag.split('_');

        if (this.drawerService.storedTriggerClick === drawerParts[1] && this.drawerService.drawerState.visibility) {
            this.drawerService.close();
        } else {
            if (this.languageService.pageHasDrawerReference(drawerParts[0])) {
                this.getDrawerContent(drawerParts[0], drawerParts[1]);
            } else {
                this.getHelpText();
            }
        }

        this.drawerService.storedTriggerClick = drawerParts[1];
    }

    /**
     * get drawer content from html
     * @param pageName
     * @param elementId
     * @private
     */
    private getDrawerContent(pageName: string, elementId: string): void {
        if (!this.failedDrawerContentRequests.includes(pageName)) {
            const isDrawerVisible: boolean = this.drawerService.getDrawerVisibility();
            if (isDrawerVisible) {
                if (document.querySelector(`.drawer #${elementId}`)) {
                    this.scrollToElement(`.drawer #${elementId}`);
                } else {
                    this.getHelpText();
                }
            } else {
                const storedDrawerContent: string = this.drawerService.getStoredDrawerContent(pageName);
                if (storedDrawerContent) {
                    this.openDrawer(storedDrawerContent, elementId);
                } else {
                    this.getNewDrawerContent(pageName, elementId);
                }
            }
        }
    }

    /**
     * Get drawer content from external html file.
     * @param pageName
     * @param elementId
     * @private
     */
    private getNewDrawerContent(pageName: string, elementId: string): void {
        const folder: string = this.environment.module === this.modulesEnum.CENTRALISED_MANAGEMENT
            ? 'centralisedManagement'
            : 'loadbalancer';

        this.http.get(`/assets/${folder}/help/drawers/${pageName}.html`, { responseType: 'text' })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (drawerContent: string): void => {
                    this.drawerService.storeDrawerContent(drawerContent, pageName);
                    this.openDrawer(drawerContent, elementId);
                },
                error: (error: ErrorResponseModel): void => {
                    this.failedDrawerContentRequests.push(pageName);
                    // Fallback if no drawer html file can be found
                    this.getHelpText(error);
                }
            });
    }

    /**
     * Open the drawer with html content
     * @param drawerContent
     * @param elementId
     * @private
     */
    private openDrawer(drawerContent: string, elementId: string): void {
        const title: string = this.environment.module === this.modulesEnum.CENTRALISED_MANAGEMENT
            ? this.languageService.translate('help_portal')
            : this.languageService.translate('help_loadbalancer');
        this.drawerService.open({
            title, content: drawerContent
        });
        const timerId = setTimeout((): void => {
            this.scrollToElement(`.drawer #${elementId}`);
            clearTimeout(timerId);
        }, 0);
    }

    /**
     * Scroll to element helper
     * @param elementId
     * @private
     */
    private scrollToElement(elementId: string): void {
        const element: HTMLElement = document.querySelector(elementId);
        this.addRemoveClassesFromElements(element);
        element?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }

    /**
     * Add remove classes from elements.
     * @param element
     * @private
     */
    private addRemoveClassesFromElements(element: HTMLElement): void {
        const elements: HTMLCollectionOf<any> = document.getElementsByClassName('selected');
        [].forEach.call(elements, (item: any): void => {
            item.classList.remove('selected');
        });
        element.classList.add('selected');
    }

    /**
     * Get help text
     * @param error
     * @private
     */
    private getHelpText(error?: any): void {
        if (this.isHelpTextValid()) {
            this.drawerService.open({
                title: this.helpText.title,
                content: this.helpText.text,
            });
        } else {
            this.drawerService.close();
            this.logService.warn('HelpComponent: ',
                'Help drawer is disabled or no help text is available.',
                error);
        }
    }

    /**
     * Return a sorry message if no other message is found
     * @returns {object}
     */
    private noText(): object {
        return {
            title: 'Sorry',
            summary: 'No help text available'
        };
    }

    /**
     * Check if the help text is valid before opening the drawer.
     * @private
     */
    private isHelpTextValid(): boolean {
        return environment.helpDrawActive && this.helpText.title !== 'Sorry' && this.helpText.text?.length;
    }
}
